import React, { useState, useEffect } from 'react';
import { categories, getCategoryStats } from '../quizBank';
import { getCategoryStats as getHistoryStats, getTotalQuizzesCompleted, getCurrentStreak } from '../utils/quizHistory';
import { getEarnedBadges, getNextBadge } from '../utils/badges';

/**
 * GoldenQuizDashboard - Category selection for Golden Quiz
 *
 * Open to everyone - no entry requirements
 * Shows 7 categories with difficulty selection
 */
function GoldenQuizDashboard({ onBackToSIFISO, onStartQuiz }) {
  const [categoryStats, setCategoryStats] = useState({});
  const [historyStats, setHistoryStats] = useState({});
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timedMode, setTimedMode] = useState(false);
  const [badges, setBadges] = useState([]);
  const [nextBadge, setNextBadge] = useState(null);

  useEffect(() => {
    // Calculate question counts for each category
    const stats = {};
    categories.forEach(cat => {
      stats[cat.id] = getCategoryStats(cat.id);
    });
    setCategoryStats(stats);

    // Load quiz history stats
    setHistoryStats(getHistoryStats());
    setTotalQuizzes(getTotalQuizzesCompleted());
    setStreak(getCurrentStreak());

    // Load badges
    setBadges(getEarnedBadges());
    setNextBadge(getNextBadge());
  }, []);

  const handleCategorySelect = (categoryId, difficulty) => {
    onStartQuiz(categoryId, difficulty, timedMode);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0A0F1C',
      color: '#F5F5F5',
      paddingBottom: '60px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN QUIZ
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#A0A0A0'
              }}>
                The Quiz Place for Everybody
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '30px'
          }} />

          {/* Stats Overview */}
          {totalQuizzes > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div style={{
                backgroundColor: '#1A1F2E',
                border: '2px solid #D4AF37',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📝</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#D4AF37' }}>
                  {totalQuizzes}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#A0A0A0' }}>
                  Quizzes Completed
                </div>
              </div>

              {streak > 0 && (
                <div style={{
                  backgroundColor: '#1A1F2E',
                  border: '2px solid #F59E0B',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔥</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#F59E0B' }}>
                    {streak} {streak === 1 ? 'day' : 'days'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#A0A0A0' }}>
                    Current Streak
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Badges Section */}
          {badges.length > 0 && (
            <div style={{
              backgroundColor: '#1A1F2E',
              border: '2px solid #FFD700',
              borderRadius: '12px',
              padding: '25px',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                color: '#FFD700',
                marginBottom: '15px'
              }}>
                🏆 Your Badges ({badges.length})
              </h2>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {badges.slice(0, 6).map((badge, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#0F1419',
                      border: `2px solid ${badge.color}`,
                      borderRadius: '10px',
                      padding: '15px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      minWidth: '180px'
                    }}
                    title={badge.description}
                  >
                    <span style={{ fontSize: '2rem' }}>{badge.icon}</span>
                    <div>
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        color: badge.color
                      }}>
                        {badge.name}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#A0A0A0'
                      }}>
                        {badge.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {nextBadge && (
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  backgroundColor: '#0F1419',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${nextBadge.color}`
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#A0A0A0',
                    marginBottom: '8px'
                  }}>
                    Next Badge: <strong style={{ color: nextBadge.color }}>{nextBadge.name}</strong>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#2A2A2A',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${nextBadge.percentage}%`,
                      height: '100%',
                      backgroundColor: nextBadge.color,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#808080',
                    marginTop: '5px'
                  }}>
                    {nextBadge.progress} / {nextBadge.target} ({nextBadge.percentage}%)
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{
            backgroundColor: '#1A1F2E',
            border: '2px solid #D4AF37',
            borderRadius: '12px',
            padding: '25px',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#D4AF37',
              marginBottom: '15px'
            }}>
              📚 Test Your Knowledge
            </h2>
            <p style={{
              fontSize: '1rem',
              color: '#C0C0C0',
              lineHeight: '1.7',
              marginBottom: '10px'
            }}>
              Choose a category and difficulty level to start. Each quiz tests your understanding
              with carefully crafted questions. Track your progress and learn from explanations!
            </p>
            {totalQuizzes > 0 && (
              <p style={{
                fontSize: '0.95rem',
                color: '#FFD700',
                marginTop: '15px',
                fontWeight: '600'
              }}>
                🎯 You've completed {totalQuizzes} quiz{totalQuizzes !== 1 ? 'zes' : ''}!
              </p>
            )}
            <div style={{
              display: 'flex',
              gap: '15px',
              marginTop: '20px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                backgroundColor: '#22C55E20',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #22C55E'
              }}>
                <span style={{ color: '#22C55E', fontWeight: '600' }}>🟢 Easy:</span>
                <span style={{ color: '#C0C0C0', marginLeft: '8px' }}>20 questions</span>
              </div>
              <div style={{
                backgroundColor: '#F59E0B20',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #F59E0B'
              }}>
                <span style={{ color: '#F59E0B', fontWeight: '600' }}>🟡 Medium:</span>
                <span style={{ color: '#C0C0C0', marginLeft: '8px' }}>20 questions</span>
              </div>
              <div style={{
                backgroundColor: '#EF444420',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #EF4444'
              }}>
                <span style={{ color: '#EF4444', fontWeight: '600' }}>🔴 Hard:</span>
                <span style={{ color: '#C0C0C0', marginLeft: '8px' }}>20 questions</span>
              </div>
              <button
                onClick={() => setTimedMode(!timedMode)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: timedMode ? '#3B82F620' : '#2A2A2A',
                  border: `2px solid ${timedMode ? '#3B82F6' : '#3A3A3A'}`,
                  borderRadius: '6px',
                  color: timedMode ? '#3B82F6' : '#A0A0A0',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = timedMode ? '#3B82F640' : '#3A3A3A';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = timedMode ? '#3B82F620' : '#2A2A2A';
                }}
              >
                {timedMode ? '⏱️ Timed Mode ON' : '⏱️ Timed Mode OFF'}
              </button>
            </div>
          </div>
        </header>

        {/* Categories Grid */}
        <main>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#F5F5F5',
            marginBottom: '30px'
          }}>
            Select a Category
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '25px'
          }}>
            {categories.map(category => {
              const stats = categoryStats[category.id] || { total: 0, easy: 0, medium: 0, hard: 0 };
              const history = historyStats[category.id];
              const isAvailable = stats.total > 0;

              return (
                <div
                  key={category.id}
                  style={{
                    backgroundColor: isAvailable ? '#1A1F2E' : '#0F1419',
                    border: isAvailable ? `2px solid ${category.color}40` : '2px solid #2A2A2A',
                    borderRadius: '16px',
                    padding: '30px',
                    opacity: isAvailable ? 1 : 0.5,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Category Header */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontSize: '3rem',
                      marginBottom: '15px'
                    }}>
                      {category.icon}
                    </div>
                    <h3 style={{
                      fontSize: '1.4rem',
                      fontWeight: '700',
                      color: isAvailable ? category.color : '#5A5A5A',
                      marginBottom: '10px'
                    }}>
                      {category.name}
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: '#A0A0A0',
                      lineHeight: '1.6',
                      marginBottom: '15px'
                    }}>
                      {category.description}
                    </p>

                    {/* Question Count */}
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#808080',
                      marginBottom: history ? '10px' : '20px'
                    }}>
                      {isAvailable ? (
                        `${stats.total} questions available`
                      ) : (
                        'Coming soon...'
                      )}
                    </div>

                    {/* History Stats */}
                    {history && (
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#A0A0A0',
                        marginBottom: '20px',
                        padding: '10px',
                        backgroundColor: '#0F1419',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${category.color}`
                      }}>
                        <div style={{ marginBottom: '5px' }}>
                          📊 {history.totalAttempts} attempt{history.totalAttempts !== 1 ? 's' : ''}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          fontSize: '0.8rem'
                        }}>
                          {history.difficulties.easy.attempts > 0 && (
                            <span style={{ color: '#22C55E' }}>
                              Easy: {Math.round(history.difficulties.easy.bestScore)}%
                            </span>
                          )}
                          {history.difficulties.medium.attempts > 0 && (
                            <span style={{ color: '#F59E0B' }}>
                              Med: {Math.round(history.difficulties.medium.bestScore)}%
                            </span>
                          )}
                          {history.difficulties.hard.attempts > 0 && (
                            <span style={{ color: '#EF4444' }}>
                              Hard: {Math.round(history.difficulties.hard.bestScore)}%
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Difficulty Buttons */}
                  {isAvailable ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      {stats.easy > 0 && (
                        <button
                          onClick={() => handleCategorySelect(category.id, 'easy')}
                          style={{
                            padding: '12px 20px',
                            backgroundColor: '#22C55E20',
                            color: '#22C55E',
                            border: '2px solid #22C55E',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#22C55E';
                            e.currentTarget.style.color = '#0A0F1C';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#22C55E20';
                            e.currentTarget.style.color = '#22C55E';
                          }}
                        >
                          🟢 Easy ({stats.easy} questions)
                        </button>
                      )}

                      {stats.medium > 0 && (
                        <button
                          onClick={() => handleCategorySelect(category.id, 'medium')}
                          style={{
                            padding: '12px 20px',
                            backgroundColor: '#F59E0B20',
                            color: '#F59E0B',
                            border: '2px solid #F59E0B',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F59E0B';
                            e.currentTarget.style.color = '#0A0F1C';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F59E0B20';
                            e.currentTarget.style.color = '#F59E0B';
                          }}
                        >
                          🟡 Medium ({stats.medium} questions)
                        </button>
                      )}

                      {stats.hard > 0 && (
                        <button
                          onClick={() => handleCategorySelect(category.id, 'hard')}
                          style={{
                            padding: '12px 20px',
                            backgroundColor: '#EF444420',
                            color: '#EF4444',
                            border: '2px solid #EF4444',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#EF4444';
                            e.currentTarget.style.color = '#0A0F1C';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#EF444420';
                            e.currentTarget.style.color = '#EF4444';
                          }}
                        >
                          🔴 Hard ({stats.hard} questions)
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#2A2A2A',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#808080',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      Questions coming soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </main>

        {/* Footer */}
        <footer style={{
          marginTop: '60px',
          paddingTop: '30px',
          borderTop: '1px solid #2A2A2A',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#808080',
            lineHeight: '1.6'
          }}>
            Golden Quiz is open to everyone. Test your knowledge, learn from explanations,
            <br />
            and challenge yourself across multiple categories and difficulty levels.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenQuizDashboard;
